/**
 * Sticky Add to Cart Banner
 * Slides up/down to reveal actual add-to-cart button at bottom of viewport
 */

// ============================================
// CONFIGURATION - Modifiez ces valeurs ici
// ============================================
const SCROLL_CONFIG = {
  // MOBILE: Espace en dessous du bouton (pour montrer le slider de réassurance)
  // Augmentez pour remonter plus haut, diminuez pour remonter moins
  mobileBottomSpace: 0, // en pixels
  
  // DESKTOP: Position du bouton par rapport au haut de l'écran
  // Valeur négative = au-dessus du viewport
  // Valeur positive = en dessous du haut du viewport
  // Exemple: -10 = 10px au-dessus, 0 = tout en haut, 50 = 50px en dessous du haut
  desktopTopOffset: 10000, // en pixels
  
  // Point de rupture pour mobile/desktop
  mobileBreakpoint: 990 // en pixels
};
// ============================================

class StickyATCBanner {
  constructor() {
    this.banner = null;
    this.productForm = null;
    this.actualATCButton = null;
    this.isExpanded = false;
    this.productInfoContainer = null;
    this.reassuranceBar = null;
    this.reassuranceVisible = false;

    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Find the product form and actual ATC button
    this.productForm = document.querySelector('product-form');
    if (!this.productForm) return;

    this.actualATCButton = this.productForm.querySelector('[type="submit"]');
    if (!this.actualATCButton) return;

    this.productInfoContainer = document.querySelector('.product__info-container');
    if (!this.productInfoContainer) return;

    // Create the sticky banner
    this.createBanner();
    
    // Set up observers
    this.setupScrollObserver();
    this.setupVariantObserver();
    this.setupReassuranceObserver();
    
    // Initial update
    this.updateBannerContent();
  }

  createBanner() {
    // Create banner HTML
    this.banner = document.createElement('div');
    this.banner.className = 'sticky-atc-banner';
    this.banner.innerHTML = `
      <div class="sticky-atc-banner__inner">
        <div class="sticky-atc-banner__content">
          <div class="sticky-atc-banner__image"></div>
          <div class="sticky-atc-banner__info">
            <div class="sticky-atc-banner__title"></div>
          </div>
        </div>
        <button type="button" class="sticky-atc-banner__button">
          <span class="sticky-atc-banner__button-text">Choose yours <span class="button-arrow-spacer" aria-hidden="true"></span><span class="text-arrow">  →</span> </span>
        </button>
      </div>
    `;

    // Add to body
    document.body.appendChild(this.banner);

    // Add click handler to toggle
    this.banner.querySelector('.sticky-atc-banner__button').addEventListener('click', (e) => {
      this.toggleBanner(e);
    });

    // Close when clicking outside (when expanded)
    document.addEventListener('click', (e) => {
      if (this.isExpanded && !this.banner.contains(e.target) && !this.productInfoContainer.contains(e.target)) {
        this.collapseBanner();
      }
    });
  }

  toggleBanner(e) {
    e.preventDefault();
    e.stopPropagation();

    // Always scroll to the actual button — no toggle.
    // Toggling caused a desync on mobile where every other tap
    // would call collapseBanner() (a no-op) instead of scrolling.
    this.expandBanner();
  }

  expandBanner() {
    // Re-query DOM references to avoid stale elements
    // (Shopify can re-render sections on variant change)
    const freshForm = document.querySelector('product-form');
    if (freshForm) {
      this.productForm = freshForm;
      const freshButton = freshForm.querySelector('[type="submit"]');
      if (freshButton) this.actualATCButton = freshButton;
    }

    this.isExpanded = true;
    this.banner.classList.add('is-expanded');

    const actualButtonRect = this.actualATCButton.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const bannerHeight = this.banner.offsetHeight;
    
    // Vérifier si on est sur mobile ou desktop
    const isMobile = window.innerWidth < SCROLL_CONFIG.mobileBreakpoint;
    
    let scrollTarget;
    if (isMobile) {
      // MOBILE: Positionner pour montrer le slider de réassurance en dessous
      const targetY = viewportHeight - bannerHeight - actualButtonRect.height - SCROLL_CONFIG.mobileBottomSpace;
      const currentY = actualButtonRect.top;
      const scrollAmount = currentY - targetY;
      scrollTarget = window.scrollY + scrollAmount;
    } else {
      // DESKTOP: Positionner selon l'offset configuré
      const currentButtonTop = actualButtonRect.top + window.scrollY;
      scrollTarget = currentButtonTop - SCROLL_CONFIG.desktopTopOffset;
    }

    scrollTarget = Math.max(0, scrollTarget);

    if (isMobile) {
      // Manual smooth scroll on mobile — the native behavior: 'smooth'
      // locks touch input during the animation on iOS Safari, leaving
      // the user unable to scroll after the animation.
      this.animateScrollTo(scrollTarget);
    } else {
      window.scrollTo({
        top: scrollTarget,
        behavior: 'smooth'
      });
    }
  }

  animateScrollTo(target, duration = 400) {
    const start = window.scrollY;
    const distance = target - start;
    if (Math.abs(distance) < 1) return;

    const startTime = performance.now();

    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      // Set scrollTop directly — bypasses the window.scrollTo override
      // in preventVariantScroll so this works even during the blockScroll
      // window and avoids per-frame function call overhead.
      document.documentElement.scrollTop = Math.round(start + distance * eased);
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }

  collapseBanner() {
    this.isExpanded = false;
    this.banner.classList.remove('is-expanded');
  }

  setupScrollObserver() {
    let ticking = false;
    
    const checkVisibility = () => {
      const actualButtonRect = this.actualATCButton.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Show banner only when actual button is COMPLETELY out of view:
      // - Above viewport: bottom of button is above top of viewport
      // - Below viewport: top of button is below bottom of viewport
      const isAboveViewport = actualButtonRect.bottom < 0;
      const isBelowViewport = actualButtonRect.top > viewportHeight;

      // Hide the banner once the footer reassurance bar is on screen so it
      // never overlaps the reassurance content at the bottom of the page.
      if ((isAboveViewport || isBelowViewport) && !this.reassuranceVisible) {
        this.banner.classList.add('is-visible');
      } else {
        this.banner.classList.remove('is-visible');
        // Auto-collapse when scrolling back to actual button
        if (this.isExpanded) {
          this.collapseBanner();
        }
      }
      
      ticking = false;
    };

    this.checkVisibility = checkVisibility;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(checkVisibility);
        ticking = true;
      }
    }, { passive: true });

    // Initial check
    checkVisibility();
  }

  setupReassuranceObserver() {
    // Watch the footer reassurance bar; hide the sticky banner while it is
    // visible so the two never overlap at the bottom of the page.
    this.reassuranceBar = document.querySelector('.reassurance-bar');
    if (!this.reassuranceBar || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver((entries) => {
      this.reassuranceVisible = entries.some((entry) => entry.isIntersecting);
      if (typeof this.checkVisibility === 'function') {
        this.checkVisibility();
      }
    });

    observer.observe(this.reassuranceBar);
  }

  setupVariantObserver() {
    // Listen for variant changes
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
      window.subscribe(window.PUB_SUB_EVENTS.variantChange, () => {
        this.updateBannerContent();
      });
    }

    // Also watch for changes to the variant input
    const variantInput = this.productForm.querySelector('[name="id"]');
    if (variantInput) {
      variantInput.addEventListener('change', () => {
        this.updateBannerContent();
      });
    }

    // Watch for price changes in the actual button
    const observer = new MutationObserver(() => {
      this.updateBannerContent();
    });

    const buttonTextSpan = this.actualATCButton.querySelector('span');
    if (buttonTextSpan) {
      observer.observe(buttonTextSpan, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
  }

  updateBannerContent() {
    // Get product info
    const productInfo = this.productForm.closest('product-info');
    if (!productInfo) return;

    // Update image
    const productImage = productInfo.querySelector('.product__media-item:not([hidden]) img');
    const bannerImage = this.banner.querySelector('.sticky-atc-banner__image');
    if (productImage && bannerImage) {
      // MoBe 22/10/2026 : vignette de 45 px. On demande l'image en 180 px (assez pour un ecran 3x),
      // et non en 1946 px (le src de la galerie) : une dizaine de Ko au lieu de 150 a 250 Ko, a
      // chaque fiche et a chaque changement de variante (ordinateur et tablette ; masquee sur telephone).
      const src = productImage.src;
      const thumb = /[?&]width=\d+/.test(src)
        ? src.replace(/([?&])width=\d+/, '$1width=180')
        : src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=180';
      bannerImage.style.backgroundImage = `url(${thumb})`;
    }

    // Update title
    const productTitle = productInfo.querySelector('.product__title h1, .product__title h2');
    const bannerTitle = this.banner.querySelector('.sticky-atc-banner__title');
    if (productTitle && bannerTitle) {
      bannerTitle.textContent = productTitle.textContent.trim();
    }

    // Texte du bouton : "Get yours" par défaut. Quand le produit est
    // indisponible, on conserve l'état réel du bouton (Sold out, etc.).
    const actualButtonText = this.actualATCButton.querySelector('span');
    const bannerButtonText = this.banner.querySelector('.sticky-atc-banner__button-text');
    if (bannerButtonText) {
      let label = 'Choose yours';
      if (this.actualATCButton.hasAttribute('disabled') && actualButtonText) {
        const fullText = actualButtonText.textContent;
        const arrowIndex = fullText.indexOf('→');
        label = (arrowIndex > -1 ? fullText.substring(0, arrowIndex) : fullText).trim() || 'Choose yours';
      }
      bannerButtonText.innerHTML = label + ' <span class="button-arrow-spacer" aria-hidden="true"></span><span class="text-arrow">  →</span>';
    }

    // Update disabled state
    if (this.actualATCButton.hasAttribute('disabled')) {
      this.banner.querySelector('.sticky-atc-banner__button').setAttribute('disabled', '');
      this.banner.classList.add('is-disabled');
    } else {
      this.banner.querySelector('.sticky-atc-banner__button').removeAttribute('disabled');
      this.banner.classList.remove('is-disabled');
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StickyATCBanner();
  });
} else {
  new StickyATCBanner();
}

// Reinitialize on section load (for theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new StickyATCBanner();
  });
}